'use client';

import Marquee from 'react-fast-marquee';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import {
  LogoStaticSliceDefault,
  LogoStaticSliceDefaultPrimary,
  LogoStaticSliceAnimatedDouble,
  LogoStaticSliceAnimatedPhotos,
  LogoStaticSliceSingleForSubpage,
  LogoStaticSlicePartnersCompanies,
  LogoStaticSliceAnimatedSingle,
  LogoStaticSliceSingleForSubPageExpanded,
} from '../../../prismicio-types';
import type * as prismic from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { useIsMobile } from '@/hooks';

/**
 * Props for `LogoStatic`.
 */
export type LogoStaticProps = SliceComponentProps<Content.LogoStaticSlice>;
export type StaticVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceDefault
>;
export type AnimatedDoubleVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceAnimatedDouble
>;

export type AnimatedPhotosVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceAnimatedPhotos
>;

export type SingleForSubpageVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceSingleForSubpage
>;

export type AnimatedSingleVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceAnimatedSingle
>;

export type PartnersCompaniesVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSlicePartnersCompanies
>;

export type SingleForSubPageExpandedVariation = prismic.SharedSlice<
  'logo_static',
  LogoStaticSliceSingleForSubPageExpanded
>;

/**
 * Component for "LogoStatic" Slices.
 */
const Logos = ({ slice }: LogoStaticProps) => {
  const autoFill = !useIsMobile();

  const baseClass = 'mx-auto';

  const getClassByVariation = (variation: string) => {
    switch (variation) {
      case 'default':
      case 'singleForSubpage':
      case 'partnersCompanies':
      case 'singleForSubPageExpanded':
        return `${baseClass} max-w-screen-2xl`;
      case 'animatedDouble':
      case 'animatedPhotos':
        return baseClass;
      case 'animatedSingle':
        return 'bg-[#B6F6EA]';
      default:
        return baseClass;
    }
  };

  const className = getClassByVariation(slice.variation);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={className}
    >
      {(slice.variation === 'default' ||
        slice.variation === 'singleForSubpage' ||
        slice.variation === 'singleForSubPageExpanded' ||
        slice.variation === 'partnersCompanies') &&
        staticLogosVariation(slice, autoFill)}
      {(slice.variation === 'animatedDouble' ||
        slice.variation === 'animatedPhotos') &&
        animatedDoubleLogosVariation(slice, autoFill)}
      {slice.variation === 'animatedSingle' &&
        animatedSingleLogosVariation(slice, autoFill)}
    </section>
  );
};

const renderLogos = (
  logos: LogoStaticSliceDefaultPrimary['main_row'] = [],
  variation: string = 'default',
) => {
  const getClassesForVariation = () => {
    if (variation === 'default') {
      return 'h-auto grow my-4 mx-8';
    }
    if (variation === 'animatedPhotos') {
      return 'max-h-[260px] mx-2 rounded-[20px] overflow-hidden bg-red-300 first:ml-4';
    }
    return 'h-auto first:ml-11 grow m-4';
  };

  return (
    <div
      className={`h-auto flex justify-center md:flex-nowrap lg:flex-nowrap w-full ${variation !== 'animatedPhotos' ? `${variation === 'partnersCompanies' ? 'gap-10' : 'gap-0'}` : 'gap-4'}`}
    >
      {logos.map(({ logo }, index) => {
        return (
          <PrismicNextImage
            key={`logo_${index}`}
            field={logo}
            className={`self-center ${getClassesForVariation()} ${variation === 'animatedPhotos' ? 'h-[260px] object-contain w-fit' : 'w-full'}`}
          />
        );
      })}
    </div>
  );
};

const staticLogosVariation = (
  slice:
    | StaticVariation
    | SingleForSubpageVariation
    | PartnersCompaniesVariation
    | SingleForSubPageExpandedVariation,
  autoFill: boolean,
) => {
  return (
    <div
      className={`
    ${
      ['partnersCompanies', 'singleForSubPageExpanded'].includes(
        slice.variation,
      )
        ? 'mb-8 mt-8 lg:flex'
        : 'mx-5 flex md:mx-16 bg-marquee-gradient rounded-[20px] overflow-hidden py-[70px] flex-col items-center backdrop-blur-[47.5px] gap-4'
    }
    ${
      slice.variation === 'default'
        ? 'justify-center'
        : 'md:py-[15px] md:px-16 md:flex-row md:gap-0 justify-between'
    }
    ${slice.variation === 'singleForSubPageExpanded' ? 'items-center' : ''}

  `}
    >
      <p
        className={`text-white text-base 
        ${slice.variation === 'default' ? 'md:text-2xl max-w-lg text-center' : 'text-lg'} 
        ${slice.variation === 'partnersCompanies' ? 'mx-8 mb-8 text-center lg:mx-0 lg:mb-0 lg:text-left lg:w-2/3 lg:font-normal' : 'font-bold'}
        ${slice.variation === 'singleForSubPageExpanded' ? 'hidden md:block' : ''}
        `}
      >
        {slice.primary.title}
      </p>
      <div className="md:hidden">
        <Marquee speed={85} autoFill={autoFill} direction="left">
          {renderLogos(slice.primary.main_row, slice.variation)}
        </Marquee>
      </div>
      <div
        className={`hidden md:flex w-full ${slice.variation === 'singleForSubpage' ? 'justify-end' : 'px-16'}`}
      >
        {renderLogos(slice.primary.main_row, slice.variation)}
      </div>
    </div>
  );
};

const animatedDoubleLogosVariation = (
  slice: AnimatedDoubleVariation | AnimatedPhotosVariation,
  autoFill: boolean,
) => {
  return (
    <div className="flex flex-col justify-center items-center gap-[22px] md:gap-11 py-12 md:py-20 overflow-hidden">
      <div
        className={`flex flex-col ${slice.variation === 'animatedDouble' ? 'px-5 gap-[22px] md:gap-11 w-full md:items-center' : 'md:flex-row justify-between w-full md:px-16 items-center gap-4 md:gap-0'}`}
      >
        <div
          className={`flex md:flex-col gap-4 px-5 md:items-start ${slice.variation === 'animatedDouble' ? 'w-full' : 'md:min-w-[306px]'}`}
        >
          {slice.variation === 'animatedPhotos' && (
            <PrismicNextImage field={slice.primary.icon} />
          )}
          <h2
            className={`text-white text-h3 w-full ${slice.variation === 'animatedDouble' ? 'text-center' : 'md:text-left'}`}
          >
            {slice.primary.title}
          </h2>
        </div>
        <p
          className={`text-white text-sm md:text-lg max-w-3xl  w-full ${slice.variation === 'animatedDouble' ? 'md:text-center font-normal md:font-bold' : 'text-center md:text-left text-lg md:font-normal'}`}
        >
          {slice.primary.description}
        </p>
      </div>
      <div
        className={`flex flex-col ${slice.variation === 'animatedDouble' ? 'gap-[22px] md:gap-11' : 'gap-4'}`}
      >
        <Marquee
          speed={32}
          autoFill={autoFill}
          direction="left"
          className="w-full"
        >
          {renderLogos(slice.primary.main_row, slice.variation)}
        </Marquee>
        <Marquee speed={32} autoFill={autoFill} direction="right">
          {renderLogos(slice.primary.secondary_row, slice.variation)}
        </Marquee>
      </div>
    </div>
  );
};

const animatedSingleLogosVariation = (
  slice: AnimatedSingleVariation,
  autoFill: boolean,
) => {
  return (
    <div className="relative overflow-hidden pb-10">
      <Marquee
        speed={32}
        autoFill={autoFill}
        direction="left"
        className="w-full"
      >
        {renderLogos(slice.primary.main_row, slice.variation)}
      </Marquee>
    </div>
  );
};

export default Logos;
